import { JxsComponent } from '@jxs/component';

if (!customElements.get('jxs-header-mega')) {
  /** 桌面端导航容器选择器（见 snippets/header-menu.liquid） */
  const DESKTOP_MENU_SELECTOR = '.header__menu-wrapper[data-main-menu] .header__inline-menu ul.list-menu-desktop';

  /**
   * 移动端抽屉导航容器选择器（见 snippets/header-drawer.liquid）。
   *
   * 只匹配一级列表：`.menu-drawer__navigation > ul`，
   * 避免把二级、三级子菜单里的 ul.menu-drawer__menu 也算进来。
   */
  const DRAWER_MENU_SELECTOR = '#Drawer-Menu .menu-drawer__navigation > ul.menu-drawer__menu';

  /**
   * 移动端断点。
   *
   * 与 jxs-header-mega.css 中的媒体查询、主题 header 的 lg 断点保持一致：
   * 990px 起显示桌面端 .list-menu-desktop，以下显示汉堡菜单（.lg\:none）。
   */
  const MOBILE_MEDIA_QUERY = '(max-width: 1024px)';

  /** 宿主 <li> 标记类，配合 CSS 的 position:static */
  const HOST_CLASS = 'jxs-mega-host';

  /** 抽屉内承载面板的 .menu-drawer__inner-submenu 标记类 */
  const DRAWER_HOST_CLASS = 'jxs-mega-drawer-host';

  /** 面板类名，CSS 依赖此类做定位与显隐 */
  const PANEL_CLASS = 'jxs-header-mega__panel';

  /** 面板处于移动端抽屉内时附加的类名，用于切换成流式布局 */
  const DRAWER_PANEL_CLASS = 'jxs-header-mega__panel--drawer';

  /** 展开状态属性（挂在宿主 <li> 上） */
  const OPEN_ATTR = 'data-jxs-mega-open';

  /** 被本组件接管/抑制的主题元素标记，便于排查与还原 */
  const SUPPRESSED_ATTR = 'data-jxs-mega-suppressed';

  /** 由本组件合成的 <details> 标记（原导航项没有子菜单时） */
  const SYNTHETIC_ATTR = 'data-jxs-mega-synthetic';

  /**
   * 模拟 Liquid 的 `handle` 过滤器，保证前后端匹配键一致。
   *
   * 规则：去除音标 → 转小写 → 非字母数字替换为连字符 → 折叠连字符 → 去除首尾连字符。
   *
   * @param {string} value
   * @returns {string}
   */
  function handleize(value) {
    if (!value) return '';

    return String(value)
      .normalize('NFD')
      // 去除组合音标符号，让 Café → cafe
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * 归一化非 ASCII 文本，用于 handle 化后为空时的兜底比对。
   *
   * 中文、日文等菜单名经 Liquid 的 `handle` 处理后会变成空字符串，
   * 此时退回到"去空白 + 转小写"的原文比对。
   *
   * @param {string} value
   * @returns {string}
   */
  function normalizeText(value) {
    if (!value) return '';
    return String(value).replace(/\s+/g, '').toLowerCase();
  }

  /**
   * 读取导航项的可见文本。
   *
   * 桌面端两种结构（snippets/header-menu.liquid）：
   * - 无子菜单：<a id="HeaderMenu-x"><span class="text">标题</span></a>
   * - 有子菜单：<summary><a class="text">标题</a>…</summary>
   *
   * 移动端抽屉两种结构（snippets/header-drawer.liquid）：
   * - 无子菜单：<a id="HeaderDrawer-x">标题</a>
   * - 有子菜单：<summary id="HeaderDrawer-x">标题<span class="svg-wrapper">…</span></summary>
   *
   * 优先取 `.text`，避免把 caret 图标、角标（new/sale label）文本一起算进去；
   * 抽屉里没有 `.text` 包装，退回读取 <a>/<summary> 的文本，并剔除
   * 内联 SVG 图标所在的 .svg-wrapper（其本身通常无文本，剔除后更稳妥）。
   *
   * @param {Element} li
   * @returns {string}
   */
  function readMenuItemText(li) {
    const textNode = li.querySelector(':scope > a .text, :scope > details > summary .text');
    if (textNode) return textNode.textContent || '';

    const fallback = li.querySelector(':scope > a, :scope > details > summary');
    if (!fallback) return '';

    // 不改动真实 DOM：克隆一份再剔除图标容器
    const clone = /** @type {Element} */ (fallback.cloneNode(true));
    clone.querySelectorAll('.svg-wrapper, svg').forEach((node) => node.remove());

    return clone.textContent || '';
  }

  /**
   * 读取面板的匹配键，直接取自 dataset。
   *
   * 不使用 JxsHeaderMegaDropdown 的 getter：`innerHTML` 重建 section 时
   * 自定义元素按树序升级，父组件的 connectedCallback 会早于子面板升级执行，
   * 此时 getter 尚不存在，读出来会是 undefined。改读 dataset 后，
   * 无论面板是否已升级都能拿到正确值。
   *
   * @param {HTMLElement} panel
   * @returns {string}
   */
  function readTrigger(panel) {
    return handleize(panel.dataset.trigger || '');
  }

  /**
   * @param {HTMLElement} panel
   * @returns {string}
   */
  function readTriggerText(panel) {
    return normalizeText(panel.dataset.triggerText || '');
  }

  /**
   * @param {HTMLElement} panel
   * @returns {string}
   */
  function readTriggerLabel(panel) {
    return panel.dataset.trigger || panel.dataset.triggerText || '(空)';
  }

  /**
   * 下拉面板。
   *
   * 注意：本组件实例会被父组件从原位置搬移到 header 的 <li> 中，
   * 搬移后它不再是 <jxs-header-mega> 的后代，父组件的 refs 会自动失效，
   * 因此父组件必须提前对 refs 做快照。
   */
  class JxsHeaderMegaDropdown extends JxsComponent {
    connectedCallback() {
      super.connectedCallback();
      this.classList.add(PANEL_CLASS);
    }
  }

  customElements.define('jxs-header-mega-dropdown', JxsHeaderMegaDropdown);

  /**
   * 调度器：把内部的面板按 trigger 匹配并搬移到 header 导航项中，
   * 然后实现展开交互。
   *
   * 桌面端与移动端共用同一批面板 DOM，按视口切换挂载目标：
   * - 桌面端（≥990px）：挂到 .list-menu-desktop 的 <li>，hover 展开。
   * - 移动端（<990px）：挂到抽屉 .menu-drawer__inner-submenu，
   *   复用主题 MenuDrawer 的点击/滑入/返回交互。
   *
   * 之所以不复制两份 DOM 分别挂载：面板内可能包含带 id 的元素、
   * 懒加载图片、表单等，复制会产生重复 id 与重复请求。
   */
  class JxsHeaderMega extends JxsComponent {
    /**
     * refs 快照。必须在搬移 DOM 前采集，原因见 JxsHeaderMegaDropdown 的注释。
     *
     * 元素可能尚未升级为 JxsHeaderMegaDropdown（见 readTrigger 的注释），
     * 因此这里按 HTMLElement 处理，一律通过 readTrigger* 读取匹配键。
     * @type {HTMLElement[]}
     */
    #panels = [];

    /** 已绑定过委托事件的菜单容器，避免重复绑定 */
    #boundMenus = new WeakSet();

    /** 当前展开的宿主 <li> @type {HTMLElement | null} */
    #openHost = null;

    /**
     * 已挂载的面板记录，用于卸载时把 DOM 还原。
     *
     * 面板被搬到 header 后就不再是本组件的后代，本组件被移除时
     * 浏览器不会连带清理它们，必须显式记录并回收，否则编辑器里
     * 每次重载都会在导航项里堆积一份旧面板。
     *
     * @type {Array<{
     *   mode: 'desktop' | 'mobile',
     *   host: HTMLElement,
     *   panel: HTMLElement,
     *   details: HTMLElement | null,
     *   activateEvent: string | null,
     *   content: HTMLElement | null,
     *   contentDisplay: string,
     *   synthetic: HTMLElement | null,
     *   originalLink: HTMLElement | null
     * }>}
     */
    #mounted = [];

    /**
     * 当前挂载模式。null 表示尚未挂载。
     * @type {'desktop' | 'mobile' | null}
     */
    #mode = null;

    /**
     * 视口监听器，用于在断点变化时切换挂载目标。
     * @type {MediaQueryList | null}
     */
    #mobileQuery = null;

    /** @type {ReturnType<typeof setTimeout> | null} */
    #openTimer = null;

    /** @type {ReturnType<typeof setTimeout> | null} */
    #closeTimer = null;

    /** 清理函数集合，disconnectedCallback 时统一执行 @type {Array<() => void>} */
    #cleanups = [];

    /** @type {number} */
    #openDelay = 80;

    /** @type {number} */
    #closeDelay = 160;

    /** @type {boolean} */
    #debug = false;

    connectedCallback() {
      super.connectedCallback();

      this.#openDelay = Number(this.dataset.openDelay ?? 80);
      this.#closeDelay = Number(this.dataset.closeDelay ?? 160);
      this.#debug = this.dataset.debug === 'true';

      // 搬移前先快照，之后一律使用 #panels
      this.#snapshotPanels();
      this.#mount();
      this.#bindViewportHook();

      // 编辑器里改动 header 其它 section 会重建导航 DOM，
      // 面板需要跟着重新挂载，否则会消失
      if (window.Shopify?.designMode) {
        this.#bindDesignModeHooks();
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.#clearTimers();
      this.#unmountAll();
      this.#cleanups.forEach((fn) => fn());
      this.#cleanups = [];
    }

    /**
     * 按当前视口选择挂载目标。
     */
    #mount() {
      const mobile = this.#isMobile();

      this.#mode = mobile ? 'mobile' : 'desktop';
      this.#log(`挂载模式：${mobile ? '移动端抽屉' : '桌面端'}`);

      if (mobile) {
        this.#mountMobile();
      } else {
        this.#mountDesktop();
      }
    }

    /**
     * @returns {boolean}
     */
    #isMobile() {
      // 优先复用已创建的 MediaQueryList，避免重复解析
      if (this.#mobileQuery) return this.#mobileQuery.matches;
      return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
    }

    /**
     * 断点变化时切换挂载目标。
     *
     * 同一批面板 DOM 不能同时存在于桌面导航和抽屉里，
     * 因此跨断点时必须先卸载再挂到另一侧。
     */
    #bindViewportHook() {
      const query = window.matchMedia(MOBILE_MEDIA_QUERY);
      this.#mobileQuery = query;

      const onChange = () => {
        const next = query.matches ? 'mobile' : 'desktop';

        // 断点抖动（如缩放时的连续事件）不必重复搬移
        if (next === this.#mode) return;

        this.#log(`视口切换至${next === 'mobile' ? '移动端' : '桌面端'}，重新挂载面板`);

        this.#closeNow();
        this.#unmountAll();
        this.#snapshotPanels();
        this.#mount();
      };

      query.addEventListener('change', onChange);

      this.#cleanups.push(() => query.removeEventListener('change', onChange));
    }

    /**
     * 采集面板快照。
     *
     * 直接查 DOM 而不用 `this.refs.dropdowns`：refs 由 MutationObserver
     * 异步刷新，而"卸载 → 快照 → 重新挂载"是同步执行的，此刻 refs 仍是
     * 搬移前的旧值（面板已不在组件内，refs 为空），会导致重新挂载时
     * 一个面板都采集不到。改为同步查询，两种时机都准确：
     * - 首次挂载：面板是本组件的后代（block 渲染结果）。
     * - 重新挂载：#unmountAll 已把面板搬回本组件下。
     *
     * 另外此处不依赖自定义元素是否已升级（见 readTrigger 的注释），
     * 标签选择器对未升级的元素同样有效。
     */
    #snapshotPanels() {
      this.#panels = /** @type {HTMLElement[]} */ (
        Array.from(this.querySelectorAll('jxs-header-mega-dropdown'))
      );

      this.#log(`采集到 ${this.#panels.length} 个下拉面板`);
    }

    /**
     * 桌面端：把所有面板搬移到匹配的导航项 <li> 中。
     */
    #mountDesktop() {
      const menu = this.#pickMenu(DESKTOP_MENU_SELECTOR, '桌面端导航容器');
      if (!menu) return;

      this.#forEachPanel(menu, (host, panel, label) => this.#attachPanel(host, panel, label));

      this.#bindDelegation(menu);
    }

    /**
     * 移动端：把面板搬进抽屉里对应一级项的子菜单容器。
     *
     * 不自己实现开合：注入主题原生的 .menu-drawer__inner-submenu 后，
     * 点击、滑入动画、返回按钮、焦点陷阱全部由主题 MenuDrawer 负责。
     */
    #mountMobile() {
      const menu = this.#pickMenu(DRAWER_MENU_SELECTOR, '移动端抽屉导航容器');
      if (!menu) return;

      this.#forEachPanel(menu, (host, panel, label) => this.#attachPanelMobile(host, panel, label));
    }

    /**
     * 选出要挂载到的菜单容器。
     *
     * @param {string} selector
     * @param {string} description 日志用的容器描述
     * @returns {HTMLElement | null}
     */
    #pickMenu(selector, description) {
      const menus = document.querySelectorAll(selector);

      if (!menus.length) {
        this.#warn(`未找到${description}（${selector}），面板无法挂载`);
        return null;
      }

      if (menus.length > 1) {
        this.#log(`发现 ${menus.length} 个${description}，仅挂载到第一个`);
      }

      // 多个 header（如吸顶副本）时只处理第一个：DOM 节点无法同时存在于多处
      return /** @type {HTMLElement} */ (menus[0]);
    }

    /**
     * 遍历面板，完成匹配后交给具体的挂载策略。
     *
     * @param {HTMLElement} menu
     * @param {(host: HTMLElement, panel: HTMLElement, label: string) => void} attach
     */
    #forEachPanel(menu, attach) {
      for (const panel of this.#panels) {
        const trigger = readTrigger(panel);
        const triggerText = readTriggerText(panel);
        const label = readTriggerLabel(panel);

        // handle 与原文都为空，说明商家没填触发器
        if (!trigger && !triggerText) {
          this.#warn('有面板未填写「菜单触发器」，已跳过');
          continue;
        }

        const host = this.#findMenuHost(menu, trigger, triggerText, label);

        if (!host) {
          this.#warn(`未找到名称匹配「${label}」的一级导航项，该面板已跳过。请检查触发器与导航菜单名称是否一致。`);
          continue;
        }

        attach(host, panel, label);
      }
    }

    /**
     * 查找匹配的一级导航项。三级降级策略。
     *
     * 桌面端宿主是 <li>，移动端抽屉宿主是 <menu-drawer class="menu-drawer__list-item">，
     * 因此用 `:scope > *` 遍历直接子元素，而非硬编码标签名。
     *
     * @param {HTMLElement} menu
     * @param {string} trigger handle 化后的匹配键，可能为空（中文菜单名）
     * @param {string} triggerText 归一化原文，用于非 ASCII 兜底
     * @param {string} label 日志用的原始文本
     * @returns {HTMLElement | null}
     */
    #findMenuHost(menu, trigger, triggerText, label) {
      // 桌面端输出 id="HeaderMenu-{handle}"（header-menu.liquid:52 / :106），
      // 抽屉输出 id="HeaderDrawer-{handle}"（header-drawer.liquid:66 / :171）
      const idPrefix = this.#mode === 'mobile' ? 'HeaderDrawer-' : 'HeaderMenu-';

      // ① 直接命中主题输出的 id，最快且最准确。
      //    用带引号的属性选择器而非 #id：handle 可能以数字开头，
      //    那样的 id 不是合法的 CSS 标识符，属性选择器可以规避转义问题。
      if (trigger) {
        const byId = menu.querySelector(`[id="${idPrefix}${trigger}"]`);
        const hostById = this.#closestMenuItem(byId, menu);

        if (hostById) {
          this.#log(`「${label}」通过 id 匹配成功`);
          return hostById;
        }
      }

      const items = /** @type {HTMLElement[]} */ (Array.from(menu.querySelectorAll(':scope > *')));

      // ② handle 化文本比对，容忍 link.handle 与显示名称不一致的情况
      if (trigger) {
        for (const item of items) {
          if (handleize(readMenuItemText(item)) === trigger) {
            this.#log(`「${label}」通过 handle 化文本匹配成功`);
            return item;
          }
        }
      }

      // ③ 原文归一化比对，支持中文等 handle 化后为空的菜单名
      if (triggerText) {
        for (const item of items) {
          if (normalizeText(readMenuItemText(item)) === triggerText) {
            this.#log(`「${label}」通过原文匹配成功`);
            return item;
          }
        }
      }

      return null;
    }

    /**
     * 从命中的 id 元素回溯到菜单容器的直接子元素（即一级导航项）。
     *
     * 抽屉里 id="HeaderDrawer-x" 也会出现在二三级子菜单的链接上
     * （形如 HeaderDrawer-{link}-{child}），但那些不是 menu 的直接子元素，
     * 逐级向上直到父节点是 menu，可确保只命中一级项。
     *
     * @param {Element | null} start
     * @param {HTMLElement} menu
     * @returns {HTMLElement | null}
     */
    #closestMenuItem(start, menu) {
      let node = start;

      while (node && node !== menu) {
        if (node.parentElement === menu) return /** @type {HTMLElement} */ (node);
        node = node.parentElement;
      }

      return null;
    }

    /**
     * 将面板插入宿主 <li>，并接管主题原有的下拉行为。
     *
     * @param {HTMLElement} host
     * @param {HTMLElement} panel
     * @param {string} label 日志用的原始触发器文本
     */
    #attachPanel(host, panel, label) {
      if (host.contains(panel)) return;

      host.classList.add(HOST_CLASS);

      // 面板可能尚未升级为自定义元素（见 readTrigger 的注释），
      // 此时它自己的 connectedCallback 还没跑过，类名要在这里补上，
      // 否则 CSS 的定位与显隐规则不生效。升级后重复添加无副作用。
      panel.classList.add(PANEL_CLASS);

      // 若该导航项自带主题 mega/下拉，需要让其失效，避免两个面板同时弹出
      const themeDetails = /** @type {HTMLElement | null} */ (host.querySelector(':scope > details'));

      /** @type {string | null} */
      let activateEvent = null;
      /** @type {HTMLElement | null} */
      let themeContent = null;
      let contentDisplay = '';

      if (themeDetails) {
        // 记录原值，卸载时还原，避免永久破坏主题自带下拉
        activateEvent = themeDetails.getAttribute('activate-event');

        // 移除 hover 触发标记，令主题的 MegaMenuDetails 不再响应悬停
        themeDetails.removeAttribute('activate-event');
        themeDetails.setAttribute(SUPPRESSED_ATTR, 'true');

        themeContent = /** @type {HTMLElement | null} */ (
          themeDetails.querySelector(':scope > .mega-menu__content, :scope > .details__list')
        );

        if (themeContent) {
          contentDisplay = themeContent.style.display;
          themeContent.style.display = 'none';
        }

        this.#log(`「${label}」已接管主题自带下拉`);
      }

      host.appendChild(panel);

      this.#mounted.push({
        mode: 'desktop',
        host,
        panel,
        details: themeDetails,
        activateEvent,
        content: themeContent,
        contentDisplay,
        synthetic: null,
        originalLink: null,
      });

      this.#log(`「${label}」面板挂载完成`);
    }

    /**
     * 移动端：把面板注入抽屉一级项的子菜单容器。
     *
     * 两种情形：
     * - 该导航项本身有子菜单：主题已经渲染好 <details> + .menu-drawer__submenu，
     *   直接把原有的 <ul> 隐藏、面板插进 .menu-drawer__inner-submenu。
     * - 该导航项没有子菜单（只是一个 <a>）：合成一份同构的 <details>，
     *   让主题的 MenuDrawer 能识别并接管开合。
     *
     * @param {HTMLElement} host 抽屉里的 <menu-drawer class="menu-drawer__list-item">
     * @param {HTMLElement} panel
     * @param {string} label 日志用的原始触发器文本
     */
    #attachPanelMobile(host, panel, label) {
      if (host.contains(panel)) return;

      panel.classList.add(PANEL_CLASS, DRAWER_PANEL_CLASS);

      let details = /** @type {HTMLElement | null} */ (host.querySelector(':scope > details'));

      /** @type {HTMLElement | null} */
      let synthetic = null;
      /** @type {HTMLElement | null} */
      let originalLink = null;

      if (!details) {
        // 导航项没有子菜单，合成结构让主题 MenuDrawer 接管
        const built = this.#buildDrawerDetails(host);

        if (!built) {
          this.#warn(`「${label}」在移动端抽屉里的结构无法识别，该面板已跳过`);
          return;
        }

        details = built.details;
        synthetic = built.details;
        originalLink = built.link;

        this.#log(`「${label}」已为无子菜单项合成抽屉结构`);
      }

      const inner = /** @type {HTMLElement | null} */ (details.querySelector('.menu-drawer__inner-submenu'));

      if (!inner) {
        this.#warn(`「${label}」未找到抽屉子菜单容器（.menu-drawer__inner-submenu），该面板已跳过`);
        return;
      }

      // 主题原有的子菜单列表先隐藏，避免与自定义面板重复展示。
      // 保留「返回」按钮（.menu-drawer__close-button），它负责收起。
      const themeList = /** @type {HTMLElement | null} */ (inner.querySelector(':scope > ul.menu-drawer__menu'));
      let contentDisplay = '';

      if (themeList) {
        contentDisplay = themeList.style.display;
        themeList.style.display = 'none';
        themeList.setAttribute(SUPPRESSED_ATTR, 'true');
      }

      inner.classList.add(DRAWER_HOST_CLASS);
      inner.appendChild(panel);

      this.#mounted.push({
        mode: 'mobile',
        host: inner,
        panel,
        details: null,
        activateEvent: null,
        content: themeList,
        contentDisplay,
        synthetic,
        originalLink,
      });

      // 合成的 <details> 里有新的 summary / button，
      // 需要让主题 MenuDrawer 重新绑定点击事件
      if (synthetic) this.#rebindDrawer(host);

      this.#log(`「${label}」移动端面板挂载完成`);
    }

    /**
     * 为「没有子菜单的一级导航项」合成抽屉子菜单结构。
     *
     * 结构完全对齐 snippets/header-drawer.liquid 的输出，
     * 这样主题的 MenuDrawer 与 component-menu-drawer.css 无需改动即可工作：
     *
     *   <details>
     *     <summary class="menu-drawer__menu-item …">标题 <span class="svg-wrapper">…</span></summary>
     *     <div class="menu-drawer__submenu has-submenu motion-reduce" tabindex="-1">
     *       <div class="menu-drawer__inner-submenu">
     *         <button class="menu-drawer__close-button …">标题</button>
     *         <ul class="menu-drawer__menu list-menu"><li><a class="… link--go-to">标题</a></li></ul>
     *       </div>
     *     </div>
     *   </details>
     *
     * @param {HTMLElement} host
     * @returns {{ details: HTMLElement, link: HTMLElement } | null}
     */
    #buildDrawerDetails(host) {
      const link = /** @type {HTMLElement | null} */ (host.querySelector(':scope > a'));
      if (!link) return null;

      const title = readMenuItemText(host).trim();
      const href = link.getAttribute('href') || '';

      // 复用主题链接上的类名，保证字体、间距、入场动画完全一致
      const linkClass = link.className;

      // 箭头图标：复制主题在其它项上渲染的 .svg-wrapper，避免硬编码 SVG 源码
      const iconSource = host
        .closest('.menu-drawer__navigation')
        ?.querySelector('.menu-drawer__list-item > details > summary > .svg-wrapper');

      const details = document.createElement('details');
      details.setAttribute(SYNTHETIC_ATTR, 'true');

      const summary = document.createElement('summary');
      summary.className = linkClass;
      // 沿用原 <a> 的 id，让 #findMenuHost 的 id 匹配在重新挂载时依然命中
      summary.id = link.id;
      summary.textContent = title;
      if (iconSource) summary.appendChild(iconSource.cloneNode(true));

      const submenu = document.createElement('div');
      submenu.className = 'menu-drawer__submenu has-submenu motion-reduce';
      submenu.setAttribute('tabindex', '-1');

      const inner = document.createElement('div');
      inner.className = 'menu-drawer__inner-submenu';

      // 返回按钮：MenuDrawer.bindEvents 会给 <button> 绑 onCloseButtonClick
      const back = document.createElement('button');
      back.className = 'menu-drawer__close-button link link--text focus-inset';
      back.setAttribute('aria-expanded', 'true');
      if (iconSource) back.appendChild(iconSource.cloneNode(true));
      back.appendChild(document.createTextNode(title));

      inner.appendChild(back);

      // 原链接仍需可达：保留一个「前往」入口，与主题 link--go-to 一致
      if (href && href !== '#') {
        const list = document.createElement('ul');
        list.className = 'menu-drawer__menu list-menu';
        list.setAttribute('role', 'list');

        const item = document.createElement('li');
        const goTo = document.createElement('a');
        goTo.className = `${linkClass} link--go-to`;
        goTo.setAttribute('href', href);
        goTo.textContent = title;

        item.appendChild(goTo);
        list.appendChild(item);
        inner.appendChild(list);
      }

      submenu.appendChild(inner);
      details.appendChild(summary);
      details.appendChild(submenu);

      // 原 <a> 隐藏而非删除，卸载时可原样还原
      link.style.display = 'none';
      link.setAttribute(SUPPRESSED_ATTR, 'true');

      host.appendChild(details);

      return { details, link };
    }

    /**
     * 让主题 MenuDrawer 重新绑定内部的 summary / button 点击事件。
     *
     * 主题在 constructor 里调用 bindEvents()，之后新增的节点不会自动绑定，
     * 因此合成结构后需要显式再调一次。这是主题的公开约定
     * （见 assets/global.js 的 MenuDrawer.bindEvents，facets.js 也这样用）。
     *
     * @param {HTMLElement} host
     */
    #rebindDrawer(host) {
      // 抽屉的一级项本身就是 <menu-drawer>；用 closest 兼容结构调整
      const drawer = /** @type {any} */ (host.closest('menu-drawer'));

      if (drawer && typeof drawer.bindEvents === 'function') {
        drawer.bindEvents();
        return;
      }

      this.#warn('未能重新绑定抽屉事件，合成的下拉项可能无法点击展开');
    }

    /**
     * 卸载所有面板，把 DOM 还原到搬移前的状态。
     *
     * 面板本身搬回宿主容器而不是直接删除：编辑器重载 header 时
     * 本组件可能并未重建，面板需要留着供下一次挂载复用。
     */
    #unmountAll() {
      for (const record of this.#mounted) {
        const { mode, host, panel, details, activateEvent, content, contentDisplay, synthetic, originalLink } = record;

        // 宿主属于 header/抽屉，不属于本 section，新旧实例会共用它。
        // 编辑器重载时 Shopify 先插入新 section、再移除旧 section，
        // 因此本方法执行时新实例可能已经接管了同一个宿主。
        // 若宿主里存在不属于自己的面板，说明已被接管，
        // 此时绝不能还原宿主状态，否则会清掉新实例刚设好的
        // .jxs-mega-host 类名，导致 hover 失效（必须保存才恢复）。
        const hostPanels = host.querySelectorAll(`:scope > .${PANEL_CLASS}`);
        const takenOver = Array.from(hostPanels).some((item) => item !== panel);

        if (takenOver) {
          this.#log('宿主已被新实例接管，跳过状态还原');
        } else if (mode === 'mobile') {
          host.classList.remove(DRAWER_HOST_CLASS);

          if (content) {
            content.style.display = contentDisplay;
            content.removeAttribute(SUPPRESSED_ATTR);
          }

          // 合成的结构整体删除，并把原 <a> 显示回来
          if (synthetic) {
            synthetic.remove();

            if (originalLink) {
              originalLink.style.display = '';
              originalLink.removeAttribute(SUPPRESSED_ATTR);
            }
          }
        } else {
          host.removeAttribute(OPEN_ATTR);
          host.classList.remove(HOST_CLASS);

          if (details) {
            details.removeAttribute(SUPPRESSED_ATTR);
            if (activateEvent !== null) details.setAttribute('activate-event', activateEvent);
          }

          if (content) content.style.display = contentDisplay;
        }

        // 面板从抽屉搬回来时要去掉移动端类名，避免下次挂到桌面端仍是流式布局
        panel.classList.remove(DRAWER_PANEL_CLASS);

        // 搬回仓库，等待下一次挂载
        if (panel.parentElement !== this) this.appendChild(panel);
      }

      this.#mounted = [];
      this.#openHost = null;
    }

    /**
     * 事件委托：整个菜单容器只绑定少量监听，而非每个面板各绑一套。
     *
     * 之所以监听容器的 mouseleave 而不是每个 <li>：面板是 <li> 的子元素且通栏，
     * 鼠标从菜单移到面板上时不会离开容器，天然实现"移到面板上保持展开"。
     *
     * @param {HTMLElement} menu
     */
    #bindDelegation(menu) {
      if (this.#boundMenus.has(menu)) return;
      this.#boundMenus.add(menu);

      const onOver = (/** @type {Event} */ event) => {
        const target = /** @type {Element} */ (event.target);
        const host = target?.closest?.(`.${HOST_CLASS}`);

        if (host && menu.contains(host)) {
          this.#scheduleOpen(/** @type {HTMLElement} */ (host));
          return;
        }

        // 悬停在其它没有自定义面板的导航项上，应收起当前面板
        const otherItem = target?.closest?.('li');
        if (otherItem && menu.contains(otherItem)) {
          this.#scheduleClose();
        }
      };

      const onLeave = () => this.#scheduleClose();


      menu.addEventListener('mouseover', onOver);
      menu.addEventListener('mouseleave', onLeave);

      this.#cleanups.push(() => {
        menu.removeEventListener('mouseover', onOver);
        menu.removeEventListener('mouseleave', onLeave);
      });
    }

    /**
     * 延迟展开。防抖避免快速划过菜单时面板连续闪烁。
     *
     * @param {HTMLElement} host
     */
    #scheduleOpen(host) {
      // 已经展开则只需取消待执行的收起
      if (this.#openHost === host) {
        this.#clearCloseTimer();
        return;
      }

      this.#clearCloseTimer();

      if (this.#openTimer) clearTimeout(this.#openTimer);
      this.#openTimer = setTimeout(() => this.#openNow(host), this.#openDelay);
    }

    /**
     * 延迟收起。留出时间让鼠标从菜单移动到面板。
     */
    #scheduleClose() {
      if (this.#openTimer) {
        clearTimeout(this.#openTimer);
        this.#openTimer = null;
      }

      if (!this.#openHost) return;

      this.#clearCloseTimer();
      this.#closeTimer = setTimeout(() => this.#closeNow(), this.#closeDelay);
    }

    /**
     * @param {HTMLElement} host
     */
    #openNow(host) {
      this.#clearTimers();

      // 同一时刻只允许一个面板展开
      if (this.#openHost && this.#openHost !== host) {
        this.#openHost.removeAttribute(OPEN_ATTR);
      }

      host.setAttribute(OPEN_ATTR, 'true');
      this.#openHost = host;
    }

    #closeNow() {
      this.#clearTimers();

      if (this.#openHost) {
        this.#openHost.removeAttribute(OPEN_ATTR);
        this.#openHost = null;
      }
    }

    #clearCloseTimer() {
      if (this.#closeTimer) {
        clearTimeout(this.#closeTimer);
        this.#closeTimer = null;
      }
    }

    #clearTimers() {
      if (this.#openTimer) {
        clearTimeout(this.#openTimer);
        this.#openTimer = null;
      }
      this.#clearCloseTimer();
    }

    /**
     * 主题编辑器支持：header 的其它 section 重载后重新挂载面板。
     *
     * 商家改动 header 内任一 section 时，Shopify 会替换整块导航 DOM，
     * 被搬进去的面板会随之消失，必须重新挂载。
     */
    #bindDesignModeHooks() {
      const onSectionLoad = (/** @type {Event} */ event) => {
        const target = /** @type {Element} */ (event.target);

        // 本 section 自身重载：面板还在自己肚子里，connectedCallback 会处理
        if (target === this || target?.contains?.(this)) return;

        // 只关心含导航容器的 section 重载。移动端抽屉由 header 或
        // header_mobile section 渲染，两种模式各查自己的容器即可。
        const selector = this.#mode === 'mobile' ? DRAWER_MENU_SELECTOR : DESKTOP_MENU_SELECTOR;

        if (!target?.querySelector?.(selector)) return;

        this.#log('检测到 header 重载，重新挂载面板');

        // 旧 header 的 DOM 已被替换，挂载记录里的宿主已失效，
        // 先把面板收回自己肚子里再重新挂载
        this.#closeNow();
        this.#unmountAll();
        this.#snapshotPanels();
        this.#mount();
      };

      document.addEventListener('shopify:section:load', onSectionLoad);

      this.#cleanups.push(() => {
        document.removeEventListener('shopify:section:load', onSectionLoad);
      });
    }

    /** @param {string} message */
    #log(message) {
      if (this.#debug) console.log(`[jxs-header-mega] ${message}`);
    }

    /** @param {string} message */
    #warn(message) {
      console.warn(`[jxs-header-mega] ${message}`);
    }
  }

  customElements.define('jxs-header-mega', JxsHeaderMega);


  /**
   * Mega 标签面板（blocks/_jxs-header-mega-tab.liquid）。
   *
   * 交互分两种模式，断点与上面 JxsHeaderMega 的 MOBILE_MEDIA_QUERY 保持一致：
   * - 桌面端（≥990px）：hover / focus 左侧标签即切换右侧内容，始终有且只有一个面板处于激活态。
   * - 移动端（<990px）：标签退化为手风琴，点击展开/收起，允许全部收起。
   *
   * 所有面板都在 DOM 中原地渲染，切换只改 data-active / aria-* / inert 属性，
   * 显隐与动画全部交给 jxs-header-mega.css，不做 DOM 搬移，也不发请求。
   */
  /**
   * 与 jxs-header-mega.js 顶部的 MOBILE_MEDIA_QUERY 及 CSS 里的断点必须一致。
   * 该常量定义在上面的 if 块作用域内，此处无法复用，改动时需同步三处。
   */
  const TAB_MOBILE_MEDIA_QUERY = '(max-width: 1024px)';
  
  /**
   * @typedef {object} MegaTabRefs
   * @property {HTMLElement[]} [tabs] 左侧标签按钮
   * @property {HTMLElement[]} [panels] 右侧内容面板
   */
  
  /** @extends {JxsComponent<MegaTabRefs>} */
  class JxsHeaderMegaTab extends JxsComponent {
    requiredRefs = ['tabs', 'panels'];
  
    /** @type {(() => void)[]} */
    #cleanups = [];
  
    /** @type {MediaQueryList | null} */
    #mobileQuery = null;
  
    /**
     * 当前激活的下标。移动端全部收起时为 -1。
     * @type {number}
     */
    #activeIndex = -1;

    connectedCallback() {
      super.connectedCallback();
  
      this.#cloneTabButtons();
      this.#bindTabEvents();
      this.#watchViewport();
      this.#applyDefaultState();
    }
  
    disconnectedCallback() {
      for (const cleanup of this.#cleanups) cleanup();
      this.#cleanups = [];
      this.#mobileQuery = null;
    }
  
    /** @returns {boolean} 当前是否为移动端视口。 */
    #isMobile() {
      if (this.#mobileQuery) return this.#mobileQuery.matches;
      return window.matchMedia(TAB_MOBILE_MEDIA_QUERY).matches;
    }
  
    /** @returns {HTMLElement[]} 标签按钮列表。 */
    #tabs() {
      return /** @type {HTMLElement[]} */ (this.refs.tabs ?? []);
    }
  
    /** @returns {HTMLElement[]} 面板列表。 */
    #panels() {
      return /** @type {HTMLElement[]} */ (this.refs.panels ?? []);
    }

    #cloneTabButtons() {
      this.refs.tabList.innerHTML = '';
      this.#tabs().forEach((btn, index) => {
        btn.dataset.index = index;
        btn.setAttribute('media', 'mobile');
        const clone = btn.cloneNode(true);
        clone.dataset.index = index;
        clone.setAttribute('media', 'desktop');
        clone.removeAttribute('ref');
        this.refs.tabList.append(clone);
      });
    }
  
    /**
     * 绑定标签交互。
     *
     * 事件挂在组件根节点上做委托，而不是逐个按钮绑定：
     * 面板内容由主题编辑器动态增删时不需要重新绑定。
     * pointerenter 不冒泡，因此用 pointerover 配合 closest 判定。
     */
    #bindTabEvents() {
      /** @param {Event} event @returns {number} */
      const indexFromEvent = (event, media) => {
        const target = /** @type {HTMLElement | null} */ (event.target);
        const tab = target?.closest?.(`.jxs-mega-tab__tab[media="${media}"]`);
        if (!tab) return -1;
        return parseInt(tab.dataset.index);
      };
  
      /** @param {Event} event */
      const onPointerOver = (event) => {
        if (this.#isMobile()) return;
        const index = indexFromEvent(event, 'desktop');
        if (index < 0 || index === this.#activeIndex) return;
        this.#activate(index);
      };
  
      /** @param {Event} event */
      const onClick = (event) => {
        const index = indexFromEvent(event, 'mobile');
        if (index < 0) return;
  
        // 桌面端：点击标签不做跳转也不切换（hover 已经切好了），
        // 但要阻止 button 触发所在表单的默认行为。
        event.preventDefault();
        event.stopPropagation();
  
        // 移动端：同一项再次点击则收起，实现手风琴
        this.#activate(index === this.#activeIndex ? -1 : index);
      };
  
      this.addEventListener('pointerover', onPointerOver);
      this.addEventListener('click', onClick);
  
      this.#cleanups.push(() => {
        this.removeEventListener('pointerover', onPointerOver);
        this.removeEventListener('click', onClick);
      });
    }
  
    /**
     * 监听视口切换。
     *
     * 桌面端要求恒有一项激活，移动端要求默认全部收起，
     * 两种默认态互斥，所以断点变化时必须重置，否则会出现
     * 「桌面切到移动后有一项莫名展开」这类残留状态。
     */
    #watchViewport() {
      if (typeof window.matchMedia !== 'function') return;
  
      const query = window.matchMedia(TAB_MOBILE_MEDIA_QUERY);
      this.#mobileQuery = query;
  
      const onChange = () => this.#applyDefaultState();
  
      query.addEventListener('change', onChange);
      this.#cleanups.push(() => query.removeEventListener('change', onChange));
    }
  
    /** 按当前视口套用默认激活态。 */
    #applyDefaultState() {
      this.#activate(this.#isMobile() ? -1 : 0);
    }
  
    /**
     * 激活指定下标的标签与面板。
     *
     * 只切换属性，不碰 display / height，动画交给 CSS。
     * @param {number} index 目标下标，传 -1 表示全部收起（仅移动端使用）。
     */
    #activate(index) {
      const tabs = this.querySelectorAll('.jxs-mega-tab__tab');
      const panels = this.#panels();
  
      if (tabs.length === 0) return;
  
      this.#activeIndex = index;
  
      tabs.forEach((tab, i) => {
        const active = parseInt(tab.dataset.index) === index;
        tab.toggleAttribute('data-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('aria-expanded', active ? 'true' : 'false');
      });
  
      panels.forEach((panel, i) => {
        const active = i === index;
        panel.toggleAttribute('data-active', active);
        // 隐藏面板里的链接不应被 Tab 键或读屏访问到
        panel.toggleAttribute('inert', !active);
      });
    }
  }
  
  customElements.define('jxs-header-mega-tab', JxsHeaderMegaTab);
}
